import React, { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Banknote, ArrowDownCircle, ArrowUpCircle, Loader2, Settings, CheckCircle2, Copy } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/carteira`;

/**
 * Painel financeiro real do admin - depósitos e saques só se tornam
 * saldo de verdade depois que o admin confirma aqui que o PIX
 * realmente entrou/saiu da conta da empresa. Nada é automático ainda
 * (não há integração com um provedor de pagamento real conectado) -
 * esta tela existe exatamente para dar controle manual seguro disso.
 */
const FinanceiroReal = () => {
  const [abaAtiva, setAbaAtiva] = useState("depositos");
  const [carregando, setCarregando] = useState(true);
  const [depositos, setDepositos] = useState([]);
  const [saques, setSaques] = useState([]);
  const [chavePix, setChavePix] = useState("");
  const [nomeBeneficiario, setNomeBeneficiario] = useState("");
  const [salvandoChave, setSalvandoChave] = useState(false);
  const [processando, setProcessando] = useState(null);

  const carregarDados = useCallback(async () => {
    setCarregando(true);
    try {
      const [respDep, respSaq] = await Promise.all([
        authService.fetchAuth(`${API}/solicitacoes-deposito/pendentes`),
        authService.fetchAuth(`${API}/saques/pendentes`),
      ]);
      if (respDep.ok) setDepositos(await respDep.json());
      if (respSaq.ok) setSaques(await respSaq.json());
    } catch (error) {
      console.error("Erro ao carregar financeiro:", error);
      toast.error("Não foi possível carregar as solicitações pendentes.");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const confirmarDeposito = async (solicitacaoId) => {
    const comprovante = window.prompt("Referência do comprovante PIX (opcional):", "");
    setProcessando(solicitacaoId);
    try {
      const resp = await authService.fetchAuth(`${API}/solicitacoes-deposito/${solicitacaoId}/confirmar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comprovante_referencia: comprovante || null }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao confirmar depósito");
      toast.success("Depósito confirmado! Saldo liberado para o usuário.");
      await carregarDados();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessando(null);
    }
  };

  const confirmarSaque = async (saqueId) => {
    const comprovante = window.prompt("Referência do comprovante PIX que você enviou (opcional):", "");
    setProcessando(saqueId);
    try {
      const resp = await authService.fetchAuth(`${API}/saques/${saqueId}/confirmar-pagamento`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comprovante_referencia: comprovante || null }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao confirmar saque");
      toast.success("Pagamento de saque confirmado!");
      await carregarDados();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessando(null);
    }
  };

  const salvarChavePix = async () => {
    if (!chavePix) {
      toast.error("Informe a chave PIX.");
      return;
    }
    setSalvandoChave(true);
    try {
      const resp = await authService.fetchAuth(`${API}/chave-pix-empresa`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chave_pix: chavePix, nome_beneficiario: nomeBeneficiario || "ClickHub" }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao salvar chave PIX");
      toast.success("Chave PIX da empresa configurada!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvandoChave(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Banknote className="w-7 h-7 text-emerald-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Financeiro Real (PIX)</h1>
          <p className="text-sm text-gray-400">Confirme aqui os depósitos e saques que realmente aconteceram na sua conta bancária</p>
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-gray-700">
        {[
          { id: "depositos", label: `Depósitos Pendentes (${depositos.length})`, icon: ArrowDownCircle },
          { id: "saques", label: `Saques Pendentes (${saques.length})`, icon: ArrowUpCircle },
          { id: "config", label: "Chave PIX da Empresa", icon: Settings },
        ].map((aba) => {
          const Icone = aba.icon;
          return (
            <button
              key={aba.id}
              onClick={() => setAbaAtiva(aba.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                abaAtiva === aba.id ? "border-emerald-500 text-emerald-400" : "border-transparent text-gray-400 hover:text-white"
              }`}
            >
              <Icone className="w-4 h-4" />
              {aba.label}
            </button>
          );
        })}
      </div>

      {carregando ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Carregando...
        </div>
      ) : (
        <>
          {abaAtiva === "depositos" && (
            <div className="space-y-2">
              <Card className="p-3 bg-blue-900/20 border-blue-700 mb-2">
                <p className="text-xs text-gray-300">
                  Confirme aqui só depois de ver o PIX de verdade caído na sua conta. Isso é o que libera o saldo do usuário.
                </p>
              </Card>
              {depositos.length === 0 && (
                <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
                  Nenhum depósito pendente de confirmação.
                </Card>
              )}
              {depositos.map((d) => (
                <Card key={d.id} className="p-4 bg-gray-800/50 border-gray-700 flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Ref: {d.referencia} — {d.tipo_usuario}</p>
                    <p className="text-xs text-gray-500">Usuário: {d.usuario_id}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-xl font-bold text-green-400">R$ {(d.valor || 0).toFixed(2)}</p>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => confirmarDeposito(d.id)}
                      disabled={processando === d.id}
                    >
                      {processando === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                      Confirmar recebido
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {abaAtiva === "saques" && (
            <div className="space-y-2">
              <Card className="p-3 bg-orange-900/20 border-orange-700 mb-2">
                <p className="text-xs text-gray-300">
                  Confirme aqui só depois de você fazer a transferência PIX de verdade para o fornecedor. Ele vai ver o comprovante que você informar.
                </p>
              </Card>
              {saques.length === 0 && (
                <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
                  Nenhum saque pendente de pagamento.
                </Card>
              )}
              {saques.map((s) => (
                <Card key={s.id} className="p-4 bg-gray-800/50 border-gray-700 flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{s.tipo_usuario} — {s.usuario_id}</p>
                    <p className="text-xs text-gray-500">Chave PIX destino: {s.chave_pix_destino} ({s.banco})</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-xl font-bold text-orange-400">R$ {(s.valor || 0).toFixed(2)}</p>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => confirmarSaque(s.id)}
                      disabled={processando === s.id}
                    >
                      {processando === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                      Confirmar pago
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {abaAtiva === "config" && (
            <Card className="p-6 bg-gray-800/50 border-gray-700 max-w-md">
              <p className="text-sm text-gray-400 mb-4">
                Essa é a chave PIX que aparece para afiliados e fornecedores quando eles pedem para depositar.
                Todo dinheiro cai direto aqui, na sua conta.
              </p>
              <div className="space-y-3">
                <Input placeholder="Chave PIX (CNPJ, e-mail, telefone ou aleatória)" value={chavePix} onChange={(e) => setChavePix(e.target.value)} />
                <Input placeholder="Nome do beneficiário (ex: ClickHub LTDA)" value={nomeBeneficiario} onChange={(e) => setNomeBeneficiario(e.target.value)} />
                <Button onClick={salvarChavePix} disabled={salvandoChave} className="w-full bg-emerald-600 hover:bg-emerald-700">
                  {salvandoChave ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Salvar Chave PIX
                </Button>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default FinanceiroReal;
